/* eslint-disable class-methods-use-this */
// eslint-disable-next-line max-classes-per-file
export type HttpClientError = {
  status: number;
  message?: string;
  url?: string;
};

export interface ResultError {
  code: string;
  message: string;
  httpStatusCode?: number;
  meta?: object;
  serialized?: string;
  headers?: Record<string, string>;

  httpError?: HttpClientError;
}

export type ResultFn = (inResult: Result<any>) => () => Result<any>;

export class Result<T> {
  public isSuccess: boolean;

  public isFailure: boolean;

  public error: ResultError;

  private _value: T;

  public constructor(
    isSuccess: boolean,
    error?: ResultError | undefined | null,
    value?: T
  ) {
    if (isSuccess && error) {
      throw new Error(
        'InvalidOperation: A result cannot be successful and contain an error'
      );
    }
    if (!isSuccess && !error) {
      throw new Error(
        'InvalidOperation: A failing result needs to contain an error message'
      );
    }

    this.isSuccess = isSuccess;
    this.isFailure = !isSuccess;
    this.error = error as ResultError;
    this._value = value as T;

    Object.freeze(this);
  }

  public getValue(): T {
    if (!this.isSuccess) {
      throw new Error(`${this.error.code}:${this.error.message}`);
    }

    return this._value;
  }

  public static ok<U>(value?: U): Result<U> {
    return new Result<U>(true, undefined, value);
  }

  public static fail<U>(error: ResultError): Result<U> {
    return new Result<U>(false, error);
  }

  public static combine(results: Result<any>[]): Result<any> {
    for (const result of results) {
      if (result.isFailure) return result;
    }
    return Result.ok();
  }

  public static pipe(...functions: ResultFn[]) {
    let lastResult: Result<any> = Result.ok();

    for (const fn of functions) {
      const result = fn(lastResult.getValue())();
      if (result.isFailure) {
        return Result.fail(result.error);
      }
      lastResult = result;
    }
    return lastResult;
  }
}

export type Either<L, A> = Left<L, A> | Right<L, A>;

export class Left<L, A> {
  readonly value: L;

  constructor(value: L) {
    this.value = value;
  }

  isLeft(): this is Left<L, A> {
    return true;
  }

  isRight(): this is Right<L, A> {
    return false;
  }
}

export class Right<L, A> {
  readonly value: A;

  constructor(value: A) {
    this.value = value;
  }

  isLeft(): this is Left<L, A> {
    return false;
  }

  isRight(): this is Right<L, A> {
    return true;
  }
}

export const left = <L, A>(l: L): Either<L, A> => new Left(l);

export const right = <L, A>(a: A): Either<L, A> => new Right<L, A>(a);
